import { InfoCircleOutlined } from '@ant-design/icons'
import { Descriptions, Drawer, Tooltip } from 'antd'
import { useAtom } from 'jotai'
import { isObject, last } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getPointPermission, MosaicComponent, UserAvatar } from '@/components'
import { User, usersAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, Permissions, PriorityEnum } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import {
  HistoryRecord,
  Production,
  ProductionClassification,
  ProductionFlowStatus,
  ProductionRole,
  ProductionType,
  Stage,
} from '../../production.schema'

const getBeforeValueContent = (record: HistoryRecord, t: (s: string) => string) => {
  const beforeValue = record.beforeValue
  if (!beforeValue) {
    return null
  }

  switch (record.key) {
    case 'projectType': {
      return t(`production.form.productionType.${ProductionType[beforeValue]}`)
    }
    case 'priority': {
      return t(`form.priority.${PriorityEnum[beforeValue]}`)
    }
    case 'classification': {
      return t(`production.form.classification.${ProductionClassification[beforeValue]}`)
    }
    case 'expectProductionTime':
    case 'expectTargetCheckTime':
    case 'expectDeliveryTime':
    case 'actualDeliveryTime':
    case 'confirmedDeliveryTime':
    case 'startDate':
    case 'endDate': {
      return moment(beforeValue).format('YYYY-MM-DD')
    }

    default: {
      return record.type === 'flow' ? t(`production.flowStatus.${beforeValue}.title`) : beforeValue
    }
  }
}

const getCurrentValueContent = (record: HistoryRecord, t: (s: string) => string) => {
  const currentValue = record.currentValue
  if (!currentValue) {
    return null
  }

  switch (record.key) {
    case 'projectType': {
      return t(`production.form.productionType.${ProductionType[currentValue]}`)
    }
    case 'priority': {
      return t(`form.priority.${PriorityEnum[currentValue]}`)
    }
    case 'classification': {
      return t(`production.form.classification.${ProductionClassification[currentValue]}`)
    }
    case 'expectProductionTime':
    case 'expectTargetCheckTime':
    case 'expectDeliveryTime':
    case 'actualDeliveryTime':
    case 'confirmedDeliveryTime':
    case 'startDate':
    case 'endDate': {
      return moment(currentValue).format('YYYY-MM-DD')
    }

    default: {
      return record.type === 'flow' ? t(`production.flowStatus.${currentValue}.title`) : currentValue
    }
  }
}

const getNameContent = (record: HistoryRecord, t: (s: string) => string) => {
  const [StageType, SubStageType, field] = record.key.split('.')

  let name = t(
    record.type === 'nested'
      ? `production.form.${record.key}.label`
      : record.type === 'flow'
        ? `production.flowStatus.status.title`
        : `production.detail.${record.key}`
  )

  let stagePrefix = ''
  let subStagePrefix = ''
  switch (StageType) {
    case 'Stage': {
      // 2025-04-02
      // record.key 可能的格式:
      // Stage.2024-10-01 // (历史结构)
      // Stage.2025-06-30.Stage.2 (当前结构)
      // Stage.Stage.0 (后续结构)
      const keys = record.key.split('.')
      const postfix = keys.length >= 3 ? parseInt(last(keys) || '') + 1 : last(keys) || ''
      stagePrefix = `${t('阶段')} ${postfix}`
      name = ''
      break
    }
    case Stage.PMF:
    case Stage.成熟产品:
    case Stage.新产品: {
      stagePrefix = `${Object.entries(Stage).find(([, value]) => value === StageType)?.[0]}: `

      if (field) {
        subStagePrefix = `${t(`production.form.${StageType}.${SubStageType}.label`)}: `
      }
      break
    }
  }

  return stagePrefix + subStagePrefix + name
}

export const HistoryItem: React.FC<{
  record: HistoryRecord
  user?: User
  permissions: Permissions
}> = ({ record, user, permissions }) => {
  const { t } = useTranslation()

  const [open, setOpen] = React.useState(false)

  const additions = record.additions || []

  const name = getNameContent(record, t)
  const beforeValue = getBeforeValueContent(record, t)
  let isBeforeValueObj = false
  try {
    isBeforeValueObj = isObject(JSON.parse(beforeValue || ''))
  } catch (error) {
    isBeforeValueObj = false
  }

  const currentValue = getCurrentValueContent(record, t)
  let isCurrentValueObj = false
  try {
    isCurrentValueObj = isObject(JSON.parse(currentValue || ''))
  } catch (error) {
    isCurrentValueObj = false
  }

  return (
    <div className='rounded-md cursor-pointer px-2 pt-1 pb-2 hover:(bg-[#F9F9FC])'>
      <div className='flex mb-1 gap-2 items-center'>
        <span className='font-medium'>{name}</span>
        {!!user && (
          <Tooltip title={user.email}>
            <span className='text-[12px] text-[#8A8A9A]'>{user?.name}</span>
          </Tooltip>
        )}
        <span className='text-[12px] text-[#8A8A9A]'>{moment(record.createdAt).utc().format('YYYY-MM-DD HH:mm')}</span>
      </div>
      <div className='flex text-[12px] gap-2'>
        {!!beforeValue && (
          <div className='bg-[#feeaec] rounded-sm py-0.5 px-2 line-through'>
            {
              <span style={{ wordBreak: 'break-word' }}>
                {isBeforeValueObj
                  ? Object.keys(JSON.parse(beforeValue)).map((key) => (
                      <div key={key}>{removeHtmlTag(JSON.parse(beforeValue)[key].toString())}</div>
                    ))
                  : beforeValue}
              </span>
            }
          </div>
        )}
        {!!currentValue && (
          <div className='rounded-sm bg-[#dff8ea] py-0.5 px-2' onClick={() => additions.length > 0 && setOpen(true)}>
            <span style={{ wordBreak: 'break-word' }}>
              {isCurrentValueObj
                ? Object.keys(JSON.parse(currentValue)).map((key) => (
                    <div key={key}>{removeHtmlTag(JSON.parse(currentValue)[key].toString())}</div>
                  ))
                : currentValue}
            </span>
            {additions.length > 0 ? (
              <Tooltip title={t('production.review.additionTooltip')}>
                <InfoCircleOutlined className='ml-1 text-[10px]' />
              </Tooltip>
            ) : null}
          </div>
        )}
        <Drawer title={t('production.review.addition')} placement='right' visible={open} onClose={() => setOpen(false)}>
          <Descriptions
            bordered
            column={1}
            title={
              <span>
                <span className='bg-[#feeaec] line-through'>{beforeValue}</span>
                {beforeValue && <span className='mx-2'>→</span>}
                <span className='bg-[#dff8ea]'>{currentValue}</span>
              </span>
            }
          >
            {additions.map((d) => {
              let label = d.type
              let value = removeHtmlTag(d.value)

              if (Object.values(ProductionRole).includes(d.type as any)) {
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, permissions).canRead) {
                  value = (<MosaicComponent />) as any
                } else {
                  value = (<UserAvatar size={20} showName email={value}></UserAvatar>) as any
                }
              }
              let content = <span>{value}</span>

              // 审批意见
              if (d.type === 'reviewComment') {
                label = t('production.detail.reviewComment')
              }

              if (d.type === 'predictWorkload') {
                label = t('production.detail.predictWorkload')
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, permissions).canRead) {
                  content = <MosaicComponent />
                } else {
                  try {
                    if (Number(value)) {
                      content = (
                        <div>
                          <div>
                            {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTotal')}:
                            <span className='ml-2'>{Number(value)}</span>
                          </div>
                        </div>
                      )
                    } else {
                      const _value = JSON.parse(value)
                      const values: number[] = Object.values(_value)
                      content = (
                        <div>
                          {values.map((v, index) => (
                            <div key={index}>
                              {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadStage', {
                                index: index + 1,
                              })}
                              : {v}
                            </div>
                          ))}
                          <div>
                            {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTotal')}:
                            <span className='ml-2'>{values.reduce((prev, current) => prev + current, 0)}</span>
                          </div>
                        </div>
                      )
                    }
                  } catch {
                    content = (
                      <div>
                        <div>
                          {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTotal')}:
                          <span className='ml-2'>{Number(value)}</span>
                        </div>
                      </div>
                    )
                  }
                }
              }

              if (d.type === 'confirmedDeliveryTime') {
                label = t('production.flowStatus.DevOnGoing.TOConfirm.form.confirmDeliveryTime.title')
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_确认交付时间, permissions).canRead) {
                  content = <MosaicComponent />
                }
              }

              if (d.type === 'standard') {
                label = t('production.detail.standard')
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], permissions).canRead) {
                  content = <MosaicComponent />
                }
              }

              if (d.type === 'actualDeliveryTime') {
                label = t('production.flowStatus.DevOnGoing.TOConfirm.form.actualDeliveryTime.title')
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_实际交付时间, permissions).canRead) {
                  content = <MosaicComponent />
                }
              }

              if (d.type === 'closeReason') {
                label = t('production.flowStatus.DevOnGoing.close.form.title')
              }

              if (d.type === 'deliveryContent') {
                label = t('production.flowStatus.DevOnGoing.delivery.form.title')
              }

              if (d.type === 'rejectReason') {
                if (record.currentValue === ProductionFlowStatus.项目已驳回) {
                  label = t('production.flowStatus.ProjectWaitForAudit.reject.reason')
                }
              }

              if (d.type === 'other') {
                label = t('production.flowStatus.ProductWaitForValidate.reject.other')
              }

              if (d.type === 'targetNotMet') {
                label = t('production.flowStatus.ProductWaitForValidate.reject.targetNotMet')
              }

              if (d.type === 'featureInComplete') {
                label = t('production.flowStatus.ProductWaitForValidate.reject.featureInComplete')
              }

              if (d.type === 'trackingResult') {
                label = t('production.flowStatus.TrackingResult.form.title')
              }

              if (d.type === 'isMeetWithinPredictResource') {
                label = t('production.flowStatus.ProductWaitForValidate.pass.form.predictResourceTitle')
                content = t(value === '1' ? 'yes' : 'no')
              }

              if (d.type === 'isMeetDeliverTarget') {
                label = t('production.flowStatus.ProductWaitForValidate.pass.form.targetTitle')
                content = t(value === '1' ? 'yes' : 'no')
              }

              if (d.type === 'isMeetDeliverTime') {
                label = t('production.flowStatus.ProductWaitForValidate.pass.form.timeTitle')
                content = t(value === '1' ? 'yes' : 'no')
              }

              if (d.type === 'confirmedTargetCompleteTime') {
                label = t('production.flowStatus.ProductWaitForValidate.pass.form.confirmedTargetCompleteTimeTitle')
                content = <span>{moment(value).format('YYYY-MM-DD')}</span>
              }

              if (d.type === 'deliverTargetReason') {
                label = t('production.flowStatus.ProductWaitForValidate.pass.form.targetFailedTitle')
              }

              if (d.type === 'deliverTimeReason') {
                label = t('production.flowStatus.ProductWaitForValidate.pass.form.timeFailedTitle')
              }

              if (d.type === 'predictResourceReason') {
                label = t('production.flowStatus.ProductWaitForValidate.pass.form.predictResourceFailedTitle')
              }

              return (
                <Descriptions.Item key={d.id} label={label}>
                  {content}
                </Descriptions.Item>
              )
            })}
          </Descriptions>
        </Drawer>
      </div>
    </div>
  )
}

export const ProductionReviews: React.FC<{ production: Production }> = ({ production }) => {
  const { historyRecords } = production
  const [users] = useAtom(usersAtom)

  return (
    <>
      {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_历史记录, production.permissions).canRead ? (
        historyRecords.map((r) => {
          const user = users.find((u) => u.email === r.email)
          return (
            <HistoryItem user={user} record={r} key={`${r.key}_${r.createdAt}`} permissions={production.permissions} />
          )
        })
      ) : (
        <MosaicComponent />
      )}
    </>
  )
}
